import * as React from 'react'
import { DisplayContent } from 'react-content-engine'
import { connect } from 'react-redux'
import { actions } from 'react-redux-form'

import Calendar from 'components/Calendar'
import { NumberOrRange } from 'components/CalendarMonth/CalendarMonth'
import PricesDayCell from 'components/PricesDayCell/PricesDayCell'
import PriceWithCurrency from 'components/PriceWithCurrency'
import { addMonths, isAfter, isBefore, subMonths } from 'date-fns'
import { Icon } from '../data/models/icon'
import { SiteCurrency } from '../data/models/locale'
import { AppState } from '../data/state'
import { MonthData } from '../pages/funnel/customise/wrapperFunction'

interface CalendarProps {
  prices: MonthData[]
  isDisabled?: boolean
  loading?: boolean
  departureDate: Date | null
  month: number | null
  year: number | null
  currency: SiteCurrency
  actionList?: boolean
  hideTrigger?: boolean
  minDate?: Date
  maxDate?: Date
  showCheapest: boolean
  showEasterEgg?: boolean
  onEasterEggComplete?: () => void
  icon?: Icon | string
  setDay(day: Date | null): void
  setYearAndMonth(year: number, month: number): void
}

class CalendarController extends React.Component<CalendarProps> {
  public static defaultProps = {
    icon: Icon.calendarColored,
  }

  public render() {
    let selectedDay = false ? 0 : undefined
    const date = this.props.departureDate
      ? new Date(this.props.departureDate)
      : undefined
    const month = this.props.month || new Date().getMonth()
    const year = this.props.year || new Date().getFullYear()
    const cheapestDays = this.getCheapestDays()
    if (date) {
      const departureYear = date.getFullYear()
      const departureMonth = date.getMonth() + 1
      if (year === departureYear && month === departureMonth) {
        selectedDay = this.props.departureDate
          ? new Date(this.props.departureDate).getDate()
          : undefined
      }
    }

    return (
      <>
        <Calendar
          year={this.props.year || new Date().getFullYear()}
          month={month}
          onMonthChange={this.onMonthChange}
          onDaySelect={this.onDaySelect}
          selectedDay={selectedDay}
          actionList={this.props.actionList}
          icon={this.props.icon}
          dayCell={
            <PricesDayCell
              currency={this.props.currency}
              showCheapest={this.props.showCheapest}
              disabled={this.isDayDisabled}
              getPrice={this.getPrice}
              cheapestDays={cheapestDays}
              getPill={this.getPill}
            />
          }
          loading={this.props.loading}
          disableLeftButton={this.isLeftDisabled()}
          disableRightButton={this.isRightDisabled()}
          showEasterEgg={this.props.showEasterEgg}
          onEasterEggComplete={this.props.onEasterEggComplete}
        />
      </>
    )
  }

  private getDayPrice = (day: number): number | undefined => {
    if (this.props.prices[day] !== undefined) {
      const dayData = this.props.prices[day]
      return dayData.discount_pp ? dayData.discount_pp : dayData.pp
    }
    return undefined
  }

  private getCheapestDays = () => {
    const minPriceKeys = Object.keys(this.props.prices)
    let minPriceDay = [+minPriceKeys[0]]

    minPriceKeys.forEach((k: string) => {
      const kPrice = this.getDayPrice(+k)
      const minPrice = this.getDayPrice(minPriceDay[0])
      if (kPrice && minPrice) {
        if (kPrice < minPrice) {
          minPriceDay = [+k]
        } else if (kPrice === minPrice) {
          minPriceDay.push(+k)
        }
      }
    })

    return minPriceDay
  }

  private getPrice = (day: number) => this.getDayPrice(day)

  private getPill = (day: number) => {
    const { currency } = this.props

    if (this.props.prices[day] !== undefined) {
      const dayData = this.props.prices[day]
      if (
        dayData.bookings !== undefined &&
        dayData.max_bookings !== undefined
      ) {
        if (dayData.bookings >= dayData.max_bookings) {
          return (
            <DisplayContent edit={true} contentKey="full">
              Full
            </DisplayContent>
          )
        }
        return `${dayData.bookings}/${dayData.max_bookings}`
      } else if (dayData.discount_pp) {
        return (
          <del>
            <PriceWithCurrency
              currency={currency}
              price={dayData.pp}
              short={currency === SiteCurrency.krona}
            />
          </del>
        )
      }
    }
    return null
  }

  private isDayDisabled = (day: number) => {
    if (!!this.props.isDisabled) {
      return true
    }
    if (this.props.prices[day] !== undefined) {
      const dayData = this.props.prices[day]
      if (
        dayData.bookings !== undefined &&
        dayData.max_bookings !== undefined
      ) {
        if (dayData.bookings >= dayData.max_bookings) {
          return true
        }
      }
      return false
    }
    return true
  }

  private onDaySelect = (selectedDay: NumberOrRange) => {
    const day = typeof selectedDay === 'number' ? selectedDay : selectedDay[0]
    const { month, year } = this.props
    if (
      this.props.prices[day] !== undefined &&
      !this.isDayDisabled(day) &&
      year &&
      month
    ) {
      const newDay = new Date(year, month - 1, day)
      this.props.setDay(newDay)
    }
  }

  private onMonthChange = (year: number, month: number) => {
    const chosenDate = new Date(year, month - 1, 1)
    let isPastMovingForward = false
    if (this.props.year && this.props.month) {
      const currentYear = this.props.year
      const currentMonth = this.props.month
      isPastMovingForward = isAfter(
        chosenDate,
        new Date(currentYear, currentMonth - 1, 1),
      )
    }
    if (
      isAfter(chosenDate, subMonths(new Date(), this.monthsBefore())) ||
      isPastMovingForward
    ) {
      this.props.setYearAndMonth(year, month)
    }
  }

  private isLeftDisabled = () => {
    if (this.props.year && this.props.month && this.props.minDate) {
      // 2 months to allow 1 back for easter egg
      const date = subMonths(
        new Date(this.props.year, this.props.month - 1, 1),
        this.monthsBefore(),
      )
      const minDate = new Date(this.props.minDate)
      return isBefore(date, minDate)
    }
    return false
  }

  private monthsBefore = () => {
    // 2 months to allow easter egg
    if (this.props.showEasterEgg) {
      return 2
    }
    return 1
  }

  private isRightDisabled = () => {
    if (this.props.year && this.props.month && this.props.maxDate) {
      const date = addMonths(
        new Date(this.props.year, this.props.month - 1, 1),
        1,
      )
      const maxDate = new Date(this.props.maxDate)
      return isAfter(date, maxDate)
    }
  }
}

const mapStateToProps = (state: AppState) => ({
  departureDate: state.funnelOrder.departureDate,
  year: state.funnelOrder.year,
  month: state.funnelOrder.month,
  currency: state.locale.currency,
})

export default connect(mapStateToProps, dispatch => ({
  setDay: (day: Date | null) => {
    dispatch(actions.change('funnelOrder.departureDate', day))
  },
  setYearAndMonth: (year: number, month: number) => {
    dispatch(actions.change('funnelOrder.year', year))
    dispatch(actions.change('funnelOrder.month', month))
  },
}))(CalendarController)
